/* eslint-disable max-len */
import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTimer from '../../hooks/useTimer';
import { baseMoment } from '../../helpers/dates';
import { COUNTDOWN_EVENT_DATE } from '../../helpers/constants';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '20px 0 91px 0',
    minWidth: '100%',
    width: '100%',
    maxWidth: '1190px',
    [theme.breakpoints.up('md')]: {
      minWidth: 624,
      margin: '40px 0 91px 0',
    },
  },
  countdownTitle: {
    fontWeight: 400,
    fontStyle: 'italic',
    fontSize: 16,
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(-1),
      fontSize: 20,
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
      fontSize: 30,
    },
  },
  timeUntilEvent: {
    color: theme.palette.primary.main,
    fontSize: 46,
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    letterSpacing: '0.08em',
    maxWidth: 300,
    margin: '0 auto',
    [theme.breakpoints.up('sm')]: {
      fontSize: 54,
      maxWidth: 380,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 90,
      maxWidth: 600,
    },
    '@media (min-width: 1280px)': {
      fontSize: 150,
      maxWidth: 900,
    },
  },
  number: {
    flex: 1,
  },
  separator: {
    flex: 0.5,

  },
  units: {
    color: theme.palette.primary.main,
    fontSize: 18,
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    letterSpacing: '0.15em',
    marginTop: theme.spacing(-1),
    maxWidth: 300,
    margin: `${theme.spacing(-1)}px auto 0`,
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
      maxWidth: 380,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 26,
      marginTop: theme.spacing(-2),
      maxWidth: 600,
    },
    '@media (min-width: 1280px)': {
      maxWidth: 900,
    },
  },
  unit: {
    flex: 1,
    fontWeight: '500',
  },
  // countdownTitleCompact: {
  //   marginBottom: theme.spacing(2),
  // },
  timeUntilEventCompact: {
    fontSize: 55,
    gap: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(1.5),
    },
    [theme.breakpoints.up('xs')]: {
      gap: theme.spacing(0),
    },
  },
  numberCompact: {
    flex: 'none',
  },
  separatorCompact: {
    flex: 'none',
  },
  unitsCompact: {
    fontSize: 12,
    gap: theme.spacing(4.5),
    marginTop: 0,
    [theme.breakpoints.up('sm')]: {
      gap: theme.spacing(3),
    },
  },
  unitCompact: {
    flex: 'none',
    padding: '0 12px',
  },
}));

const prependZero = (number) => (
  (number < 10) ? `0${number}` : number
);

const Countdown = ({ hideSubtitle, compact }) => {
  const classes = useStyles();

  const [timeUntilEvent, setTimeUntilEvent] = useState('');
  const eventStartDate = COUNTDOWN_EVENT_DATE;

  const onTick = useCallback((now, start, end) => {
    setTimeUntilEvent(end.diff(now));
  }, [setTimeUntilEvent, eventStartDate]);
  useTimer({ onTick, endDate: eventStartDate, step: 10000 });

  if (timeUntilEvent === '') return null;

  if (timeUntilEvent <= 0) return (
    <div className={classes.root}>
      <Typography
        className={clsx(classes.timeUntilEvent, compact && classes.timeUntilEventCompact)}
      >
        LIVE
      </Typography>
    </div>
  );

  const days = baseMoment(eventStartDate).diff(baseMoment(), 'days');
  const duration = baseMoment.duration(timeUntilEvent);
  const hours = duration.hours();
  const minutes = duration.minutes();

  return (
    <div className={classes.root}>
      {!hideSubtitle && (
        <Typography
          variant="subtitle1"
          className={clsx(classes.countdownTitle, compact && classes.countdownTitleCompact)}
        >
          See you there in
        </Typography>
      )}
      <Typography
        className={clsx(classes.timeUntilEvent, compact && classes.timeUntilEventCompact)}
      >
        <span className={clsx(classes.number, compact && classes.numberCompact)}>{prependZero(days)}</span>
        <span className={clsx(classes.separator, compact && classes.separatorCompact)}>:</span>
        <span className={clsx(classes.number, compact && classes.numberCompact)}>{prependZero(hours)}</span>
        <span className={clsx(classes.separator, compact && classes.separatorCompact)}>:</span>
        <span className={clsx(classes.number, compact && classes.numberCompact)}>{prependZero(minutes)}</span>
      </Typography>
      <Typography
        className={clsx(classes.units, compact && classes.unitsCompact)}
      >
        <span className={clsx(classes.unit, compact && classes.unitCompact)}>DAYS</span>
        <span className={clsx(classes.separator, compact && classes.separatorCompact)}>&nbsp;</span>
        <span className={clsx(classes.unit, compact && classes.unitCompact)}>HOURS</span>
        <span className={clsx(classes.separator, compact && classes.separatorCompact)}>&nbsp;</span>
        <span className={clsx(classes.unit, compact && classes.unitCompact)}>MIN</span>
      </Typography>
    </div>
  );
};

Countdown.propTypes = {
  hideSubtitle: PropTypes.bool,
  compact: PropTypes.bool,
};

Countdown.defaultProps = {
  hideSubtitle: false,
  compact: false,
};

export default Countdown;
